/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import userDetail from "Stores/userDetail";
import Utils from "Utils/utils";
import { makeApi, URLS } from "Utils/apiURL";
import { INSTALL_PLUGIN_URL, MP_EVENT, SERVER_URL } from "Static/constant";
import { validatePluginInstalled } from "Utils/chromeExtValidator";
import FeatureGrid from "./FeatureGrid";
import { BrandMark, SgMark } from "./brandMarks";

/**
 * Settings → Integrations → Chrome Extension.
 * Not installed: what it does and an install button; after the click the page watches for the
 * extension by itself (no "I've installed it" button) and moves on when it answers.
 * Installed: version, a pin tip (only when the extension says it isn't pinned), a setup checklist
 * from states the app already knows (CRM, dialer number, inbox), and what you can do.
 */
const POLL_MS = 2000;
const POLL_FOR_MS = 10 * 60 * 1000;
const PIN_TIP_KEY = "sgExtPinTipDismissed";

const readLocal = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};
const writeLocal = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // private window / blocked storage: the tip just comes back next time
  }
};

const PUZZLE = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 12h-2a2 2 0 1 0-4 0h-2V9a2 2 0 1 0 0-4V3H4v6a2 2 0 1 1 0 4v8h8v-2a2 2 0 1 1 4 0v2h4z" />
  </svg>
);
const PIN = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 17v5M9 3h6l-1 6 4 4H6l4-4-1-6z" />
  </svg>
);
const TICK = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m5 12 5 5 9-10" />
  </svg>
);

const CRM_NAMES = { hubspot: "HubSpot", salesforce: "Salesforce", zoho: "Zoho", pipedrive: "Pipedrive" };

const WorksOn = () => (
  <div className="ceWorksOn">
    Works on
    <span><BrandMark name="linkedin" size={14} />LinkedIn</span>
    <span><BrandMark name="gmail" size={14} />Gmail</span>
    <span className="ceWorksOn__crms">
      <BrandMark name="hubspot" size={14} /><BrandMark name="salesforce" size={14} /><BrandMark name="zoho" size={14} /><BrandMark name="pipedrive" size={14} />CRMs
    </span>
    <span>any website</span>
  </div>
);

// The hero's browser preview: a LinkedIn profile with the Salesgear panel open.
const BrowserPreview = () => (
  <div className="ceBrowser" aria-hidden="true">
    <div className="ceBrowser__bar">
      <span className="ceBrowser__dot ceBrowser__dot--r" /><span className="ceBrowser__dot ceBrowser__dot--y" /><span className="ceBrowser__dot ceBrowser__dot--g" />
      <span className="ceBrowser__url">linkedin.com/in/priya-raman</span>
      <span className="ceBrowser__ext"><SgMark width={6} color="#fff" /><span className="ceBrowser__extBadge">✓</span></span>
    </div>
    <div className="ceBrowser__body">
      <div className="ceBrowser__page">
        <span className="ceBrowser__cover" />
        <span className="ceBrowser__avatar" />
        <b>Priya Raman</b>
        <span className="ceBrowser__sub">VP Sales · Pavilion · Austin</span>
        <span className="ceMini__bar ceMini__bar--w85" /><span className="ceMini__bar ceMini__bar--w70" /><span className="ceMini__bar ceMini__bar--w80" />
      </div>
      <div className="ceBrowser__panel">
        <div className="ceBrowser__brand"><SgMark width={9} />salesgear</div>
        <b>Priya Raman</b>
        <span className="ceBrowser__sub">VP Sales @ Pavilion</span>
        <span className="ceBrowser__row"><span className="ceMini__rowIcon ceMini__rowIcon--email" />priya@joinpavilion.com<span className="ceMini__tick" /></span>
        <span className="ceBrowser__row"><span className="ceMini__rowIcon ceMini__rowIcon--phone" />+1 512 555 0142</span>
        <span className="ceBrowser__btns"><span className="ceMini__btn">Call</span><span className="ceMini__btn ceMini__btn--ghost">Sequence</span></span>
        <span className="ceBrowser__dr">Run Deep Research</span>
      </div>
    </div>
  </div>
);

const Step = ({ n, title, desc }) => (
  <div className="ceStep">
    <span className="ceStep__n">{n}</span>
    <div>
      <div className="ceStep__title">{title}</div>
      <div className="ceStep__desc">{desc}</div>
    </div>
  </div>
);

const CheckItem = ({ done, current, title, desc, note, cta, onCta }) => (
  <div className={`ceCheck__item${current ? " is-current" : ""}${done ? " is-done" : ""}`}>
    <span className="ceCheck__dot" aria-hidden="true">{done ? TICK : null}</span>
    <div className="ceCheck__text">
      <div className="ceCheck__title">{title}</div>
      <div className="ceCheck__desc">{desc}</div>
    </div>
    {done && note ? <span className="ceCheck__note">{note}</span> : null}
    {!done && cta ? (
      <button type="button" className={`ceBtn ${current ? "ceBtn--primary" : "ceBtn--ghost"} ceBtn--sm`} onClick={onCta}>{cta}</button>
    ) : null}
  </div>
);

const ChromeExtension = observer(() => {
  const history = useHistory();
  const [version, setVersion] = useState("");
  const [pinned, setPinned] = useState(null); // null: the extension didn't say (older build)
  const [waiting, setWaiting] = useState(false);
  const [justInstalled, setJustInstalled] = useState(false);
  const [pinTipHidden, setPinTipHidden] = useState(readLocal(PIN_TIP_KEY) === "1");
  const [crms, setCrms] = useState(null);
  const [hasDialerNumber, setHasDialerNumber] = useState(null);
  const pollRef = useRef(null);
  const installed = !!userDetail.pluginInstalled || !!version;

  // The extension answers IS_PLUGIN_INSTALLED with its version (AE_PLUGIN_INSTALLED) and, from
  // 7.0.0, AE_PLUGIN_STATUS with the pinned state. chromeExtValidator already flips pluginInstalled.
  useEffect(() => {
    const onMessage = (event) => {
      const { type, data } = event?.data || {};
      if (type === "AE_PLUGIN_INSTALLED") {
        if (typeof data === "string") setVersion(data);
        userDetail.setPluginInstalled(true);
      } else if (type === "AE_PLUGIN_STATUS") {
        if (data?.version) setVersion(data.version);
        if (typeof data?.pinned === "boolean") setPinned(data.pinned);
      }
    };
    window.addEventListener("message", onMessage, false);
    validatePluginInstalled();
    return () => {
      window.removeEventListener("message", onMessage, false);
      clearInterval(pollRef.current);
    };
  }, []);

  // After "Add to Chrome": ask every 2s until the extension answers (or 10 minutes pass).
  useEffect(() => {
    if (!waiting) return undefined;
    const started = Date.now();
    pollRef.current = setInterval(() => {
      if (Date.now() - started > POLL_FOR_MS) {
        clearInterval(pollRef.current);
        setWaiting(false);
        return;
      }
      validatePluginInstalled();
    }, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [waiting]);

  useEffect(() => {
    if (waiting && installed) {
      clearInterval(pollRef.current);
      setWaiting(false);
      setJustInstalled(true);
    }
  }, [waiting, installed]);

  // Checklist states the app can read: connected CRMs and a calling number.
  useEffect(() => {
    if (!installed) return;
    makeApi({ url: `${SERVER_URL}/cm/plugin/crm-status`, method: "GET" })
      .then((res) => setCrms(res?.data && typeof res.data === "object" ? res.data : {}))
      .catch(() => setCrms({}));
    makeApi({ url: URLS.getMyPhoneNumbers, method: "GET" })
      .then((res) => {
        const list = res?.data?.myPhoneNumbers || res?.data;
        setHasDialerNumber(Array.isArray(list) ? list.length > 0 : false);
      })
      .catch(() => setHasDialerNumber(false));
  }, [installed]);

  const openStore = () => {
    Utils.mixpanelTrack(MP_EVENT.PLUGIN_INSTALL_CLICKED, { pageType: window.location.pathname });
    window.open(INSTALL_PLUGIN_URL, "_blank", "noopener");
    setWaiting(true);
  };

  const hidePinTip = () => {
    setPinTipHidden(true);
    writeLocal(PIN_TIP_KEY, "1");
  };

  if (!installed) {
    return (
      <div className="chromeExtSettings">
        <section className="ceHero">
          <div className="ceHero__copy">
            <div className="ceEyebrow"><SgMark width={9} />Chrome extension</div>
            {waiting ? (
              <>
                <h1 className="ceHero__title">Waiting for Chrome…</h1>
                <p className="ceHero__lede">
                  Click <b>Add to Chrome</b> in the tab that just opened. This page updates by itself when it’s in, no need to refresh.
                </p>
                <div className="ceHero__actions">
                  <span className="ceSpinner" aria-hidden="true" />
                  <span className="ceHero__hint">Checking every few seconds</span>
                  <button type="button" className="ceLink" onClick={openStore}>Open the store again</button>
                </div>
              </>
            ) : (
              <>
                <h1 className="ceHero__title">Salesgear on every page you sell from</h1>
                <p className="ceHero__lede">
                  Find emails and direct dials on LinkedIn, see who’s who in your Gmail threads, call from your CRM and add people to sequences, without switching tabs.
                </p>
                <div className="ceHero__actions">
                  <button type="button" className="ceBtn ceBtn--primary ceBtn--lg" onClick={openStore}>{PUZZLE}Add to Chrome</button>
                </div>
              </>
            )}
            <WorksOn />
          </div>
          <BrowserPreview />
        </section>

        <section className="ceSection">
          <h2 className="ceSection__title">How it works</h2>
          <div className="ceSteps">
            <Step n={1} title="Add to Chrome" desc="One click from the Chrome Web Store. You stay signed in with this Salesgear account." />
            <Step n={2} title="Pin it" desc="Pin Salesgear next to the address bar so its ✓ and counts are always in view." />
            <Step n={3} title="Open it anywhere" desc="Click the icon, or the thin blue line on the edge of the page, on Gmail, your CRM or any site." />
          </div>
        </section>

        <section className="ceSection">
          <h2 className="ceSection__title">What you can do with it</h2>
          <FeatureGrid installed={false} />
        </section>
      </div>
    );
  }

  const connectedCrm = crms ? Object.keys(CRM_NAMES).find((k) => crms[k]) : null;
  const hasInbox = (userDetail.connectedInboxes || []).length > 0;
  const steps = [
    { key: "install", done: true, title: "Install the extension", desc: "Detected in this browser.", note: "automatic" },
    {
      key: "pin",
      done: pinned === true,
      title: "Pin it to the toolbar",
      desc: "The icon shows ✓ and counts as you browse. Click the puzzle icon next to the address bar, then the pin beside Salesgear.",
      note: "from the extension",
    },
    {
      key: "crm",
      done: !!connectedCrm,
      title: connectedCrm ? `Connect ${CRM_NAMES[connectedCrm]}` : "Connect your CRM",
      desc: "Calls and emails from the extension log to your CRM.",
      note: "connected",
      cta: "Connect a CRM",
      onCta: () => history.push("/settings/crm"),
    },
    {
      key: "dialer",
      done: hasDialerNumber === true,
      title: "Set up a calling number",
      desc: "Call straight from the contact card on any CRM record.",
      note: "ready",
      cta: "Set up dialer",
      onCta: () => history.push("/settings/dialer"),
    },
    {
      key: "inbox",
      done: hasInbox,
      title: "Connect your inbox",
      desc: "Tracking and the thread bar in Gmail.",
      note: "connected",
      cta: "Connect inbox",
      onCta: () => history.push("/settings/account"),
    },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const currentKey = steps.find((s) => !s.done)?.key;

  return (
    <div className="chromeExtSettings">
      <section className={`ceStatus${justInstalled ? " is-new" : ""}`}>
        <span className="ceStatus__mark"><SgMark width={15} color="#fff" /><span className="ceStatus__badge">{TICK}</span></span>
        <div className="ceStatus__text">
          <div className="ceStatus__titleRow">
            <h1 className="ceStatus__title">{justInstalled ? "You’re all set" : "Chrome extension is installed"}</h1>
            {version ? <span className="ceStatus__version">Version {version}</span> : null}
          </div>
          <div className="ceStatus__desc">Signed in as you. Choose where it shows from ⚙ Settings inside the extension’s panel.</div>
        </div>
        <div className="ceStatus__actions">
          <a className="ceBtn ceBtn--primary" href="https://www.linkedin.com/feed/" target="_blank" rel="noopener noreferrer">Open LinkedIn</a>
          <a className="ceBtn ceBtn--ghost" href="https://mail.google.com/" target="_blank" rel="noopener noreferrer">Open Gmail</a>
        </div>
      </section>

      {pinned === false && !pinTipHidden ? (
        <div className="cePinTip" role="status">
          <span className="cePinTip__icon">{PIN}</span>
          <div className="cePinTip__text"><b>Pin Salesgear</b> next to the address bar to see a ✓ when someone’s already in Salesgear, and a count of people on company pages.</div>
          <button type="button" className="ceBtn ceBtn--ghost ceBtn--sm" onClick={hidePinTip}>Got it</button>
        </div>
      ) : null}

      <section className="ceCheck">
        <div className="ceCheck__head">
          <div>
            <div className="ceCheck__heading">Get the most out of the extension</div>
            <div className="ceCheck__sub">Each step unlocks something you can use today</div>
          </div>
          <div className="ceCheck__progress">
            <span className="ceCheck__track"><span className={`ceCheck__fill ceCheck__fill--${doneCount}`} /></span>
            <b>{doneCount} of {steps.length} done</b>
          </div>
        </div>
        {steps.map((s) => (
          <CheckItem key={s.key} {...s} current={s.key === currentKey} />
        ))}
      </section>

      <section className="ceSection">
        <h2 className="ceSection__title">What you can do</h2>
        <p className="ceSection__sub">Each one works the moment you open that kind of page.</p>
        <FeatureGrid installed />
      </section>
    </div>
  );
});

export default ChromeExtension;
